import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { Playlist, PlaylistSong } from '../../types';

export default function PlaylistDetail() {
  const { id } = useParams<{ id: string }>();
  const [playlist, setPlaylist] = useState<Playlist | null>(null);
  const [songs, setSongs] = useState<PlaylistSong[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentSong, setCurrentSong] = useState<PlaylistSong | null>(null);

  useEffect(() => {
    const fetchPlaylist = async () => {
      const { data: playlistData } = await supabase
        .from('playlists')
        .select('*')
        .eq('id', id)
        .single();
      setPlaylist(playlistData);

      const { data: songsData } = await supabase
        .from('playlist_songs')
        .select('*')
        .eq('playlist_id', id)
        .order('position');
      setSongs(songsData || []);
      setLoading(false);
    };
    fetchPlaylist();
  }, [id]);

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto"></div>
      </div>
    );
  }

  if (!playlist) {
    return (
      <div className="text-center py-8">
        <p className="text-[#8E8E93]">Playlist no encontrada</p>
        <Link to="/music" className="text-primary mt-2 block">Volver a música</Link>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      <Link to="/music" className="text-primary">← Volver</Link>
      
      <div className="card p-4 flex items-center gap-4">
        {playlist.cover_url ? (
          <img src={playlist.cover_url} alt={playlist.name} className="w-24 h-24 rounded-lg object-cover" />
        ) : (
          <div className="w-24 h-24 bg-primary/10 rounded-lg flex items-center justify-center">
            <span className="text-4xl">🎵</span>
          </div>
        )}
        <div>
          <h1 className="text-xl font-bold text-white">{playlist.name}</h1>
          {playlist.description && <p className="text-sm text-[#8E8E93]">{playlist.description}</p>}
          <p className="text-xs text-[#6B6B6B] mt-1">{songs.length} canciones</p>
        </div>
      </div>

      <div className="space-y-1">
        {songs.map((song) => (
          <button
            key={song.id}
            onClick={() => setCurrentSong(song)}
            className={`card w-full flex items-center gap-3  text-left ${currentSong?.id === song.id ? 'bg-primary/5 border border-primary' : ''}`}
          >
            <span className="text-lg">{currentSong?.id === song.id ? '▶️' : '🎵'}</span>
            <div className="flex-1">
              <h3 className="font-medium text-white">{song.title}</h3>
              {song.artist && <p className="text-sm text-[#8E8E93]">{song.artist}</p>}
            </div>
            <span className="text-sm text-[#6B6B6B]">{formatDuration(song.duration_seconds)}</span>
          </button>
        ))}
      </div>

      {currentSong && currentSong.audio_url && (
        <div className="fixed bottom-20 left-0 right-0 bg-white border-t border-gray-200 p-4">
          <div className="flex items-center gap-3">
            {currentSong.cover_url ? (
              <img src={currentSong.cover_url} alt={currentSong.title} className="w-12 h-12 rounded" />
            ) : (
              <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">🎵</div>
            )}
            <div className="flex-1">
              <h4 className="font-medium text-white">{currentSong.title}</h4>
              {currentSong.artist && <p className="text-sm text-[#8E8E93]">{currentSong.artist}</p>}
            </div>
          </div>
          <audio controls className="w-full mt-2" src={currentSong.audio_url} autoPlay />
        </div>
      )}
    </div>
  );
}
